import { useEffect, useState } from "react";
import { my_routes } from "../router/all_routes";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import dayjs from "dayjs";
import { getTeacherById, updateTeacher } from "../../api/teachers";
import { toast } from "sonner";
import CommonSelect from "../../core/common/commonSelect";
import { deleteAvatar, getAvatarFileById } from "../../api/avatar";
import UploadPhotoModal from "../avatar/UploadPhotoModal";
import { Popconfirm } from "antd";

const status = [
  { value: "true", label: "Active" },
  { value: "false", label: "Inactive" },
];

const sexe = [
  { value: "Masculin", label: "Masculin" },
  { value: "Feminin", label: "Feminin" },
];

// shéma du formulaire pour enregistré l'inscription d'un professeur
const teacherSchema = z.object({
  lastname: z.string().nonempty("Veuillez insérer un nom"),
  firstname: z.string().nonempty("Veuillez insérer un prénom"),

  sexe: z
    .object(
      {
        value: z.string(),
        label: z.string(),
      },
      {
        required_error: "Veuillez choisir un genre valide",
        invalid_type_error: "Veuillez choisir un genre valide",
      },
    )
    .refine(
      (val) => !!val?.value && !!val?.label,
      "Veuillez choisir un genre valide",
    ),

  birthday: z
    .any()
    .nullable()
    .optional()
    .refine(
      (val) => val === null || val === undefined || dayjs(val).isValid(),
      "Veuillez entrer une date de naissance valide",
    ),

  email: z
    .string()
    .email("Veuillez fournir une adresse email valide")
    .optional(),

  number: z.string().optional(),

  adresse: z.string().optional(),

  speciality: z.string().nullable().optional(),

  statut: z.enum(["true", "false"], {
    required_error: "Veuillez sélectionner un statut",
  }),
});

type RegisterFormFields = z.infer<typeof teacherSchema>;

const CreateTeacher = () => {
  const { id } = useParams(); // On récupère l'id de l'URL
  const [avatar, setAvatar] = useState<any>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);

  const routes = my_routes;

  const [teacherEdited, setTeacherEdited] = useState<any>();
  const navigate = useNavigate();

  const location = useLocation();

  //   Initialisation de zod avec react-hook-form pour le compte parent
  const {
    register,
    handleSubmit,
    setValue,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm<RegisterFormFields>({
    resolver: zodResolver(teacherSchema),
  });

  const getTeacherInfoForEdit = async (id_teacher: string) => {
    try {
      const response = await getTeacherById(id_teacher);

      const avatar = await getAvatarFileById("TeacherProfil", id_teacher);
      setAvatar(avatar.data);

      // console.log("teacher data: ", response.data);
      setTeacherEdited(response.data);
      setValue("birthday", response.data.birthday);
      setValue("firstname", response.data.firstname);
      setValue("lastname", response.data.lastname);
      setValue("sexe", {
        value: response.data.sexe,
        label: response.data.sexe,
      });
      setValue("speciality", response.data.speciality);
      setValue("statut", response.data.statut === true ? "true" : "false");
      setValue("number", response.data.number);
      setValue("adresse", response.data.adresse);
      setValue("email", response.data.email);
    } catch (error: any) {
      toast.error(
        error.message ||
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  useEffect(() => {
    if (id) getTeacherInfoForEdit(id);
    else toast.error("Aucun id trouvé dans l'URL.");
  }, [location.pathname]);

  const handleAddPhoto = () => {
    setShowUploadModal(true);
  };

  const handleDeletePhoto = async () => {
    try {
      if (!avatar || !id) return;
      await deleteAvatar(avatar?.id);
      toast.success("Photo supprimee avec succes");
      getTeacherInfoForEdit(id);
    } catch (error: any) {
      // console.log("Erreur lors de la suppression: ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  // Fonction de soumission du formulaire d'édition
  const onSubmitEdit: SubmitHandler<RegisterFormFields> = async (data) => {
    try {
      // console.log("edit data: ", data);
      const formData = {
        ...data,
        statut: data.statut === "true",
        email: data.email === "" ? undefined : data.email,
        sexe: data.sexe.value,
      };
      if (id) await updateTeacher(id, formData);
      else throw new Error("id introuvable dans l'URL");

      toast.success("Professeur modifié avec succès");
      navigate(routes.allTeacher);
    } catch (error: any) {
      // Gérer les erreurs de l'API
      // console.log("erreur edit prof: ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  return (
    <>
      {/* Page wrapper */}
      <div className="page-wrapper">
        <div className="content content-two">
          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="mb-1">Modifier Professeur</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to={routes.allTeacher}>Professeurs</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Modifier Professeur
                  </li>
                </ol>
              </nav>
            </div>
          </div>
          {/* /Page Header */}
          <div className="row">
            <div className="col-md-12">
              <form onSubmit={handleSubmit(onSubmitEdit)}>
                <>
                  {/* Information Personnel */}
                  <div className="card">
                    <div className="card-header bg-light">
                      <div className="d-flex align-items-center">
                        <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                          <i className="ti ti-info-square-rounded fs-16" />
                        </span>
                        <h4 className="text-dark">Informations Personnelles</h4>
                      </div>
                    </div>
                    <div className="card-body pb-1">
                      <div className="row">
                        <div className="col-md-12">
                          <div className="d-flex align-items-center flex-wrap row-gap-3 mb-3">
                            <div className="d-flex align-items-center justify-content-center avatar avatar-xxl border border-dashed me-2 flex-shrink-0 text-dark frames">
                              {avatar ? (
                                <img
                                  src={avatar?.temporaryUrl}
                                  alt={`${teacherEdited?.lastname} ${teacherEdited?.firstname}`}
                                />
                              ) : (
                                <i className="ti ti-photo-plus fs-16" />
                              )}
                            </div>
                            <div className="profile-upload">
                              <div className="profile-uploader d-flex align-items-center">
                                <div
                                  className="drag-upload-btn mb-3"
                                  onClick={() => handleAddPhoto()}
                                >
                                  Upload
                                </div>
                                <Popconfirm
                                  title="Êtes-vous sûr de vouloir supprimer cette photo ?"
                                  onConfirm={() => handleDeletePhoto()}
                                  disabled={!avatar?.temporaryUrl}
                                  okText="Oui"
                                  cancelText="Non"
                                >
                                  <Link to="#" className="btn btn-primary mb-3">
                                    Remove
                                  </Link>
                                  {/*<Button icon={<DeleteOutlined />} danger />*/}
                                </Popconfirm>
                              </div>
                              <p className="fs-12">
                                Upload image size 4MB, Format JPG, PNG, SVG
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="row row-cols-xxl-5 row-cols-md-6">
                        <div className="col-xxl col-xl-3 col-md-6">
                          <div className="mb-3">
                            <label className="form-label">Prénom</label>
                            <input
                              type="text"
                              placeholder="Entrer un prenom"
                              className={
                                errors.firstname
                                  ? "form-control is-invalid"
                                  : "form-control"
                              }
                              defaultValue={teacherEdited?.firstname}
                              {...register("firstname")}
                            />
                            {errors.firstname && (
                              <div className="invalid-feedback">
                                {errors.firstname.message}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="col-xxl col-xl-3 col-md-6">
                          <div className="mb-3">
                            <label className="form-label">Nom</label>
                            <input
                              type="text"
                              placeholder="Entrer un nom"
                              className={
                                errors.lastname
                                  ? "form-control is-invalid"
                                  : "form-control"
                              }
                              defaultValue={teacherEdited?.lastname}
                              {...register("lastname")}
                            />
                            {errors.lastname && (
                              <div className="invalid-feedback">
                                {errors.lastname.message}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="col-xxl col-xl-3 col-md-6">
                          <div className="mb-3">
                            <label className="form-label">Genre</label>
                            <CommonSelect
                              className={
                                errors.sexe || errors.sexe
                                  ? "select is-invalid"
                                  : "select"
                              }
                              onChange={(option: any) => {
                                setValue("sexe", option || "");
                                clearErrors("sexe");
                              }}
                              options={sexe}
                              defaultValue={sexe.find(
                                (sexe) => sexe.value === teacherEdited?.sexe,
                              )}
                            />
                            {errors.sexe && (
                              <div className="invalid-feedback">
                                {errors.sexe.message}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="col-xxl col-xl-3 col-md-6">
                          <div className="mb-3">
                            <label className="form-label">
                              Numéro Téléphone
                            </label>
                            <input
                              type="text"
                              placeholder="Ex. +223 84382001"
                              className={
                                errors.number
                                  ? "form-control is-invalid"
                                  : "form-control"
                              }
                              defaultValue={teacherEdited?.number}
                              {...register("number")}
                            />
                            {errors.number && (
                              <div className="invalid-feedback">
                                {errors.number.message}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="col-xxl col-xl-3 col-md-6">
                          <div className="mb-3">
                            <label className="form-label">Adresse Mail</label>
                            <input
                              type="email"
                              placeholder="Ex. example@gmail.com"
                              className={
                                errors.email
                                  ? "form-control is-invalid"
                                  : "form-control"
                              }
                              defaultValue={teacherEdited?.email}
                              {...register("email")}
                            />
                            {errors.email && (
                              <div className="invalid-feedback">
                                {errors.email.message}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="col-xxl col-xl-3 col-md-6">
                          <div className="mb-3">
                            <label className="form-label">
                              Date de Naissance
                            </label>
                            <div className="input-icon position-relative">
                              {
                                <input
                                  type="date"
                                  className={
                                    errors.birthday
                                      ? "form-control datetimepicker is-invalid"
                                      : "form-control datetimepicker"
                                  }
                                  placeholder="DD-MM-YYYY"
                                  {...register("birthday")}
                                />
                              }
                              {errors.birthday && (
                                <div className="invalid-feedback">
                                  {errors.birthday &&
                                  typeof errors.birthday.message === "string"
                                    ? errors.birthday.message
                                    : null}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="col-xxl col-xl-3 col-md-6">
                          <div className="mb-3">
                            <label className="form-label">Specialite</label>
                            <input
                              type="text"
                              placeholder="Ex. Mathématique"
                              className={
                                errors.speciality
                                  ? "form-control is-invalid"
                                  : "form-control"
                              }
                              defaultValue={teacherEdited?.speciality}
                              {...register("speciality")}
                            />
                            {errors.speciality && (
                              <div className="invalid-feedback">
                                {errors.speciality.message}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="col-xxl-3 col-xl-3 col-md-6">
                          <div className="mb-3">
                            <label className="form-label">Status</label>
                            <CommonSelect
                              className={
                                errors.statut || errors.statut
                                  ? "select is-invalid"
                                  : "select"
                              }
                              options={status}
                              defaultValue={status.find(
                                (s) =>
                                  s.value ===
                                  (teacherEdited?.statut === true
                                    ? "true"
                                    : "false"),
                              )}
                              onChange={(option: any) => {
                                setValue("statut", option?.value || "");
                                clearErrors("statut");
                              }}
                            />
                            {errors.statut && (
                              <div className="invalid-feedback">
                                {errors.statut.message}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="col-xxl-12 col-md-12 col-xl-12">
                          <div className="mb-3">
                            <label className="form-label">Adresse</label>
                            <textarea
                              placeholder="Exemple : Badalabougou, Commune 5, Rue 300 Porte 218"
                              rows={4}
                              cols={8}
                              className={
                                errors.adresse
                                  ? "form-control is-invalid"
                                  : "form-control"
                              }
                              defaultValue={teacherEdited?.adresse}
                              {...register("adresse")}
                            />
                            {errors.adresse && (
                              <div className="invalid-feedback">
                                {errors.adresse.message}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* /Information Personnel */}
                </>
                <div className="text-end">
                  <Link
                    to={routes.allTeacher}
                    type="button"
                    className="btn btn-light me-3"
                  >
                    Annuler
                  </Link>
                  {isSubmitting ? (
                    <button
                      className="btn btn-primary-light"
                      type="button"
                      disabled={true}
                    >
                      <span
                        className="spinner-border spinner-border-sm align-middle me-1 "
                        role="status"
                        aria-hidden="true"
                      />
                      Loading...
                    </button>
                  ) : (
                    <button
                      type="submit"
                      className="btn btn-primary"
                      // data-bs-dismiss="modal"
                    >
                      Enregistrer Professeur
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      {/* /Page wrapper */}
      <UploadPhotoModal
        profilId={id}
        profilType="TeacherProfil"
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onSuccess={() => {
          if (id) getTeacherInfoForEdit(id);
        }}
      />
    </>
  );
};

export default CreateTeacher;
